import { useCallback, useEffect, useState } from 'react';
import { leave, time, employees as employeeApi } from '../api/client';

/** Manager/HR queue: pending leave requests and timesheets in one place. */
export default function Approvals({ onError, onNotice }) {
  const [leaveQueue, setLeaveQueue] = useState([]);
  const [sheetQueue, setSheetQueue] = useState([]);
  const [staff, setStaff] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, t, people] = await Promise.all([
        leave.pending(), time.pending(), employeeApi.list(),
      ]);
      setLeaveQueue(l || []);
      setSheetQueue(t || []);
      const map = {};
      (people || []).forEach((p) => { map[p.id] = p; });
      setStaff(map);
    } catch (e) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  async function decide(kind, id, approve) {
    const note = approve ? null : window.prompt('Reason for rejection (optional)') || null;
    setBusy(`${kind}-${id}`);
    try {
      if (kind === 'leave') await leave.decide(id, approve, note);
      else await time.decide(id, approve, note);
      onNotice(approve ? 'Approved' : 'Rejected');
      await load();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(null);
    }
  }

  const who = (id) => {
    const e = staff[id];
    return e ? `${e.employeeCode} · ${e.fullName}` : `#${id}`;
  };

  if (loading) return <p className="muted">Loading…</p>;

  const empty = leaveQueue.length === 0 && sheetQueue.length === 0;

  return (
    <div>
      <h2>Approvals</h2>
      <p className="muted">
        Requests from everyone reporting to you, at any level. HR sees all.
      </p>

      {empty && (
        <div className="card empty">
          <p><strong>Nothing waiting on you.</strong></p>
          <p className="muted">Leave requests and submitted timesheets appear here.</p>
        </div>
      )}

      {leaveQueue.length > 0 && (
        <>
          <h3>Leave requests ({leaveQueue.length})</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th></th></tr>
              </thead>
              <tbody>
                {leaveQueue.map((r) => (
                  <tr key={r.id}>
                    <td>{who(r.employeeId)}</td>
                    <td><span className="pill">{r.leaveCode}</span></td>
                    <td className="nowrap small">
                      {r.fromDate}{r.toDate !== r.fromDate && ` → ${r.toDate}`}
                    </td>
                    <td>{r.days}</td>
                    <td className="small">{r.reason || '—'}</td>
                    <td className="nowrap">
                      <button className="btn btn-sm btn-primary" disabled={busy === `leave-${r.id}`}
                              onClick={() => decide('leave', r.id, true)}>Approve</button>
                      <button className="btn btn-sm btn-danger" disabled={busy === `leave-${r.id}`}
                              onClick={() => decide('leave', r.id, false)}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {sheetQueue.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Timesheets ({sheetQueue.length})</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Employee</th><th>Week of</th><th>Hours</th><th>Note</th><th></th></tr>
              </thead>
              <tbody>
                {sheetQueue.map((t) => (
                  <tr key={t.timesheetId}>
                    <td>{who(t.employeeId)}</td>
                    <td className="nowrap small">{t.weekStart}</td>
                    <td><strong>{t.totalHours}</strong>h</td>
                    <td className="small">{t.employeeNote || '—'}</td>
                    <td className="nowrap">
                      <button className="btn btn-sm btn-primary" disabled={busy === `sheet-${t.timesheetId}`}
                              onClick={() => decide('sheet', t.timesheetId, true)}>Approve</button>
                      <button className="btn btn-sm btn-danger" disabled={busy === `sheet-${t.timesheetId}`}
                              onClick={() => decide('sheet', t.timesheetId, false)}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
